import { getRegionConfigByCountry, getRegionConfigByCountryCodeRegion } from "custom-config/region.configs";
import { BuildingListing, Listing, ListingArea, PropertyListing } from "~/components/blocks/common/Interfaces/ListingsInterface";

export const stripAndFormat = (str: string): string => {
    return str
        .toLowerCase()
        .trim()
        .replace(/[.,/#!$%^&*;:{}=_`~()]/g, "")
        .replace(/ /g, "-")
        .replace(/'/g, "-")
        .replace(/’/g, "-")
        .replace(/-null/g, "")
        .replace(/null-/g, "")
        .replace(/-undefined/g, "")
        .replace(/undefined-/g, "")
        .replace(/-false/g, "")
        .replace(/false-/g, "")
        .replace(/[ ]+/g, "-")
        .replace(/-{2,}/g, "-")
        .replace(/[^a-zA-Z0-9-]+/g, "");
};

/** Drop sitemap lastmod suffixes like `,2025-08-19T16:11:56.000Z`. */
export function stripCommaSuffix(value?: string | number | null): string {
    return String(value ?? "").split(",")[0].trim();
}

/** Last hyphen segment of a slug param, ignoring anything after a comma. */
export function idFromSlugParam(slugParam?: string | null): string {
    const parts = stripCommaSuffix(slugParam).split("-").filter(Boolean);
    return parts[parts.length - 1] || "";
}

function buildListingSlugParts(listing: Listing | PropertyListing): string {
    return [
        listing.noOfWS > 0 ? `${listing.noOfWS}-person` : undefined,
        listing.spaceTypeFormula,
        "office",
        !listing.hideStreetNumber ? listing.streetNumber : undefined,
        listing.streetName,
        listing.city,
        listing.state && listing.state !== listing.city ? listing.state : undefined,
        listing.zipcode,
    ]
        .filter((part) => part != null && part !== "")
        .join("-");
}

export const listingSlug = (listing: Listing | PropertyListing) => {
    const listingId = stripCommaSuffix(listing.id);
    return `${getRegionUrlPrefix(listing?.country)}/listings/${stripAndFormat(buildListingSlugParts(listing))}${listingId ? `-${listingId}` : ""}`;
};
export const listingCanonical = (listing: Listing | PropertyListing) => {
    const listingId = stripCommaSuffix(listing.id);
    return stripAndFormat(buildListingSlugParts(listing)) + (listingId ? `-${listingId}` : "");
};
export const propertySlug = (property?: Listing | PropertyListing | null) => {
    if (!property) return "";
    const slug = [
        !property.hideStreetNumber ? property.streetNumber : undefined,
        property.streetName,
        property.city,
        property?.state || property?.state_picklist__c,
    ]
        .filter((part) => part != null && part !== "")
        .join("-");

    const propertyId = stripCommaSuffix(property?.id);
    return `${getRegionUrlPrefix(property?.country)}/properties/${stripAndFormat(slug)}${propertyId ? `-${propertyId}` : ""}`;
};
export const propertyCanonical = (property?: Listing | PropertyListing | null) => {
    if (!property) return "";
    const slug = [
        !property.hideStreetNumber ? property.streetNumber : undefined,
        property.streetName,
        property.city,
        property?.state,
    ]
        .filter((part) => part != null && part !== "")
        .join("-");

    const propertyId = stripCommaSuffix(property?.id);
    return stripAndFormat(slug) + (propertyId ? `-${propertyId}` : "");
};
export const buildingSlug = (building?: BuildingListing | null) => {
    if (!building) return "";
    const slug = `${building.name}-${!building.name &&
        building.street}-${building.suburb &&
        ((building.name ?? building.street) ?? '').indexOf(building.suburb) == -1 &&
        building.suburb !== building.state &&
        building.suburb}-${building.state &&
        ((building.name ?? building.street) ?? '').indexOf(building.state) == -1 &&
        building.state}
    `;
    const buildingId = stripCommaSuffix(building?.id || building?.sfid);
    return `${getRegionUrlPrefix(building?.country)}/buildings/${stripAndFormat(slug)}${buildingId ? `-${buildingId}` : ""}`;
};
export const buildingCanonical = (building?: BuildingListing | null) => {
    if (!building) return "";
    const slug = `${building.name}-${!building.name &&
        building.street}-${building.suburb &&
        ((building.name ?? building.street) ?? '').indexOf(building.suburb) == -1 &&
        building.suburb !== building.state &&
        building.suburb}-${building.state &&
        ((building.name ?? building.street) ?? '').indexOf(building.state) == -1 &&
        building.state}
    `;
    const buildingId = stripCommaSuffix(building.id || building.sfid);
    return stripAndFormat(slug) + (buildingId ? `-${buildingId}` : "");
};

export const areaCanonical = (area: ListingArea) => {
    const parts = [area.webPrimaryKeyword];
    const locationName = area.suburb || area.city;

    if (
        locationName &&
        area.state !== locationName &&
        area.webPrimaryKeyword?.indexOf(locationName) === -1
    ) {
        parts.push(locationName);
    }

    if (area.state && area.webPrimaryKeyword?.indexOf(area.state) === -1) {
        parts.push(area.state);
    }

    if (
        area.country &&
        area.country !== area.state &&
        area.webPrimaryKeyword?.indexOf(area.country) === -1
    ) {
        parts.push(area.country);
    }

    const slug = parts.filter(Boolean).join('-');
    const areaId = stripCommaSuffix(area.id);

    return `${stripAndFormat(slug)}${areaId ? `-${areaId}` : ""}`;
};
export const areaSlug = (area: ListingArea) => {
    const parts = [area.webPrimaryKeyword];
    const locationName = area.suburb || area.city;

    if (
        locationName &&
        area.state !== locationName &&
        area.webPrimaryKeyword?.indexOf(locationName) === -1
    ) {
        parts.push(locationName);
    }

    if (area.state && area.webPrimaryKeyword?.indexOf(area.state) === -1) {
        parts.push(area.state);
    }

    if (
        area.country &&
        area.country !== area.state &&
        area.webPrimaryKeyword?.indexOf(area.country) === -1
    ) {
        parts.push(area.country);
    }

    const slug = parts.filter(Boolean).join('-');
    const areaId = stripCommaSuffix(area.id);

    return `${getRegionUrlPrefix(area?.country)}/areas/${stripAndFormat(slug)}${areaId ? `-${areaId}` : ""}`;
};

export const workspaceSlug = (workspace: {
    name: string;
    id: string | number;
    sfid: string;
    country?: string;
}) => {
    const namePart = workspace?.name ?? '';
    const idPart = stripCommaSuffix(workspace?.id ?? workspace?.sfid ?? '');
    const base = `${getRegionUrlPrefix(workspace?.country)}/workspaces/${stripAndFormat(namePart)}`;
    return idPart ? `${base}-${idPart}` : base;
};
export const workspaceCanonical = (workspace?: {
    name?: string;
    id?: string | number;
    sfid?: string;
}) => {
    const namePart = workspace?.name ?? '';
    const idPart = stripCommaSuffix(workspace?.id ?? workspace?.sfid ?? '');
    const base = stripAndFormat(namePart);
    return idPart ? `${base}-${idPart}` : base;
};

export const userSlug = (name: string) => {
    return "/meet-the-team/" + stripAndFormat(name);
};

export const getRegionUrlPrefix = (country?: string): string => {
    if (!country) return '';
    if (country.toLowerCase() === 'united states') return '';
    const regionData = getRegionConfigByCountry(country);
    return `/${regionData?.region?.toLowerCase() || ''}`;
};

export const getRegionUrlPrefixWithCountryCode = (country?: string): string => {
    if (!country) return '';
    if (country.toLowerCase() === 'us') return '';
    const regionData = getRegionConfigByCountryCodeRegion(country);
    const region = regionData?.region?.toLowerCase() || '';
    return region ? `/${region}` : '';
};